import { Link } from "@tanstack/react-router";
import { ArrowRight, FlaskConical, Package } from "lucide-react";
import { CLUSTERS, type ClusterKey } from "@/lib/clusters";

/**
 * "Explore this topic" strip that links to both hubs for a cluster:
 * - Commercial hub: the product PDP for the cluster (typed <Link>).
 * - Evidence hub: the matching section on /ingredients (anchor <a>).
 *
 * Placed at the end of every blog article so each spoke passes topical
 * authority up to its two cluster hubs.
 */
export function ClusterLinks({ cluster }: { cluster: ClusterKey }) {
  const c = CLUSTERS[cluster];
  return (
    <aside
      aria-label={`Explore ${c.label}`}
      className="mt-10 grid gap-4 rounded-3xl border border-brand/15 bg-brand/5 p-6 md:grid-cols-2 md:p-7"
    >
      <div className="md:col-span-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand">
          Explore the topic
        </p>
        <h2 className="mt-1 font-display text-lg font-bold text-brand-deep md:text-xl">
          {c.label}
        </h2>
      </div>

      <Link
        to="/products/$handle"
        params={{ handle: c.productHandle }}
        className="group flex items-center justify-between gap-3 rounded-2xl bg-background p-4 ring-1 ring-border transition hover:ring-brand/40"
      >
        <span className="flex items-start gap-3">
          <Package className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
          <span>
            <span className="block text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              The formulation
            </span>
            <span className="mt-0.5 block font-display text-sm font-semibold text-brand-deep group-hover:text-brand">
              {c.productTitle}
            </span>
          </span>
        </span>
        <ArrowRight className="h-4 w-4 shrink-0 text-brand transition group-hover:translate-x-0.5" />
      </Link>

      <a
        href={`/ingredients#${c.ingredientAnchor}`}
        className="group flex items-center justify-between gap-3 rounded-2xl bg-background p-4 ring-1 ring-border transition hover:ring-brand/40"
      >
        <span className="flex items-start gap-3">
          <FlaskConical className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
          <span>
            <span className="block text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              Evidence & doses
            </span>
            <span className="mt-0.5 block font-display text-sm font-semibold text-brand-deep group-hover:text-brand">
              {c.ingredientLabel}
            </span>
          </span>
        </span>
        <ArrowRight className="h-4 w-4 shrink-0 text-brand transition group-hover:translate-x-0.5" />
      </a>
    </aside>
  );
}
